import {
  BreakEvent,
  ClockGeoRecordInput,
  CreateDaySequenceInput,
  DaySequence,
  DaySequenceChangeInput,
  DaySequenceStatus,
  ItemStatus,
} from '../generated/gql-types';
import { create } from 'zustand';
import { daySequenceActions, ClockActionResult } from './daySequence.action';
import { devtools, persist } from 'zustand/middleware';
import { createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useWorkerJobStore } from '../workerJob/useWorkerJobStore';
import { getHighestPriorityStatus } from '../util/daySequencePriority.util';
import {
  mergeDaySequencesForDateISO,
  mergeDaySequencesForLatestContext,
  mergeDaySequencesForTodayStatusPanel,
} from '../util/daySequenceDisplayMerge.util';
import { compareDaySequencesNewestFirst } from './daySequenceSort.util';
import { TimesheetStatusGroup } from './daySequence.type';

type PageState = {
  lastKey?: string | null;
  hasMore: boolean;
  isLoading: boolean;
  error?: string | null;
  fetchedAt?: number;
};

interface DaySequenceStore {
  byId: Record<string, DaySequence>;
  idsByJobId: Record<string, string[]>;
  page: Record<string, PageState>;
  inFlight: Record<string, Promise<void> | undefined>;
  staleMs: number;
  isLoading: boolean;

  upsertMany: (rows: DaySequence[]) => void;
  removeJobCache: (jobId: string) => void;

  fetchFirstPage: (jobId: string, limit?: number) => Promise<void>;
  fetchNextPage: (jobId: string, limit?: number) => Promise<void>;
  refresh: (jobId: string, limit?: number) => Promise<void>;
  fetchAllDaySequencesByJobId: (jobId: string, limit?: number) => Promise<void>;
  fetchDaySequencesForJobIds: (jobIds: string[]) => Promise<void>;
  fetchAllDaySequencesForClient: (clientId: string) => Promise<void>;
  fetchAllDaySequencesForUser: (
    userId: string,
    statuses?: DaySequenceStatus[]
  ) => Promise<void>;
  fetchAllDaySequencesRequiringAction: (tenantId: string) => Promise<void>;
  clockAction: (
    jobId: string,
    clockInTimestamp?: string,
    clockOutTimestamp?: string,
    breaks?: BreakEvent[],
    clockInGeo?: ClockGeoRecordInput,
    clockOutGeo?: ClockGeoRecordInput
  ) => Promise<ClockActionResult>;
  approveDaySequences: (ids: string[]) => Promise<DaySequence[]>;
  disputeProposedChange: (ids: string[]) => Promise<DaySequence[]>;
  proposeDaySequenceChanges: (
    daySequenceChanges: DaySequenceChangeInput[]
  ) => Promise<DaySequence[]>;
  resolveDaySequenceDisputes: (
    daySequenceChanges: DaySequenceChangeInput[]
  ) => Promise<DaySequence[]>;
  createDaySequences: (input: CreateDaySequenceInput) => Promise<DaySequence[]>;

  // Selectors
  getDaySequenceById: (id: string) => DaySequence | undefined;
  getDaySequenceByJobId: (jobId: string) => DaySequence[];
  getDaySequenceByJobIdAndDateISO: (
    jobId: string,
    dateISO: string
  ) => DaySequence | undefined;
  getTodayDaySequenceByJobId: (jobId: string) => DaySequence | undefined;
  getLatestDaySequenceByJobId: (jobId: string) => DaySequence | undefined;
  getDaySequencesForClient: (clientId: string) => DaySequence[];
  getDaySequencesForClientByStatus: (
    clientId: string,
    status: DaySequenceStatus
  ) => DaySequence[];
  getDaySequencesForClientByStatusGroupedByJob: (
    clientId: string,
    status: TimesheetStatusGroup
  ) => Record<string, DaySequence[]>;
  getDaySequencesForActiveAndCompletedJobs: () => DaySequence[];
  getDaySequencesForWorkerAction: () => DaySequence[];
  getDaySequencesForUser: (
    userId: string,
    statuses?: DaySequenceStatus[]
  ) => DaySequence[];
  getDaySequencesMeta: (jobId: string) => PageState;
}

const sortIdsForJob = (ids: string[], byId: Record<string, DaySequence>) => {
  ids.sort((a, b) => {
    const A = byId[a];
    const B = byId[b];
    if (!A || !B) {
      return 0;
    }
    return compareDaySequencesNewestFirst(A, B);
  });
};

export const useDaySequenceStore = create<DaySequenceStore>()(
  devtools(
    persist(
      (set, get) => ({
        byId: {},
        idsByJobId: {},
        page: {},
        inFlight: {},
        staleMs: 2 * 60 * 1000,
        isLoading: false,

        upsertMany: (rows) =>
          set((state) => {
            const nextById = { ...state.byId };
            const batches: Record<string, string[]> = {};

            for (const row of rows) {
              nextById[row.id] = row;
              const jobId = row.jobId;
              if (!batches[jobId])
                batches[jobId] = (state.idsByJobId[jobId] ?? []).slice();
              if (!batches[jobId].includes(row.id)) batches[jobId].push(row.id);
            }

            const nextIdsByJobId = { ...state.idsByJobId };
            for (const [jobId, ids] of Object.entries(batches)) {
              sortIdsForJob(ids, nextById);
              nextIdsByJobId[jobId] = ids;
            }

            return { byId: nextById, idsByJobId: nextIdsByJobId };
          }),

        removeJobCache: (jobId) =>
          set((state) => {
            const idsByJobId = { ...state.idsByJobId };
            const page = { ...state.page };
            delete idsByJobId[jobId];
            delete page[jobId];
            return { idsByJobId, page };
          }),

        // Actions
        fetchFirstPage: async (jobId, limit = 25) => {
          const { inFlight } = get();
          if (inFlight[jobId]) return inFlight[jobId];

          const p = (async () => {
            set((s) => ({
              page: {
                ...s.page,
                [jobId]: {
                  hasMore: true,
                  isLoading: true,
                  error: null,
                  lastKey: undefined,
                  fetchedAt: s.page[jobId]?.fetchedAt,
                },
              },
            }));

            try {
              const res = await daySequenceActions.getDaySequencesByJobId(
                jobId,
                limit,
                undefined
              );
              get().removeJobCache(jobId); // reset index before applying first page
              get().upsertMany(res.daySequences);

              set((s) => ({
                page: {
                  ...s.page,
                  [jobId]: {
                    lastKey: res.lastEvaluatedKey ?? null,
                    hasMore: res.hasMore, // <- trust server
                    isLoading: false,
                    error: null,
                    fetchedAt: Date.now(),
                  },
                },
              }));
            } catch (e: any) {
              set((s) => ({
                page: {
                  ...s.page,
                  [jobId]: {
                    ...(s.page[jobId] ?? { hasMore: true, lastKey: null }),
                    isLoading: false,
                    error: e?.message ?? 'Failed to load',
                  },
                },
              }));
            } finally {
              set((s) => ({ inFlight: { ...s.inFlight, [jobId]: undefined } }));
            }
          })();

          set((s) => ({ inFlight: { ...s.inFlight, [jobId]: p } }));
          return p;
        },
        fetchNextPage: async (jobId, limit = 25) => {
          const { page, inFlight } = get();
          const meta = page[jobId];
          if (!meta) return get().fetchFirstPage(jobId, limit);
          if (!meta.hasMore || meta.isLoading) return;
          if (inFlight[jobId]) return inFlight[jobId];

          const p = (async () => {
            set((s) => ({
              page: {
                ...s.page,
                [jobId]: { ...s.page[jobId], isLoading: true, error: null },
              },
            }));
            try {
              const res = await daySequenceActions.getDaySequencesByJobId(
                jobId,
                limit,
                meta.lastKey || undefined
              );

              get().upsertMany(res.daySequences);

              set((s) => ({
                page: {
                  ...s.page,
                  [jobId]: {
                    lastKey: res.lastEvaluatedKey ?? null,
                    hasMore: res.hasMore,
                    isLoading: false,
                    error: null,
                    fetchedAt: Date.now(),
                  },
                },
              }));
            } catch (e: any) {
              set((s) => ({
                page: {
                  ...s.page,
                  [jobId]: {
                    ...s.page[jobId],
                    isLoading: false,
                    error: e?.message ?? 'Failed to load',
                  },
                },
              }));
            } finally {
              set((s) => ({ inFlight: { ...s.inFlight, [jobId]: undefined } }));
            }
          })();

          set((s) => ({ inFlight: { ...s.inFlight, [jobId]: p } }));
          return p;
        },

        refresh: async (jobId, limit = 25) => {
          const meta = get().page[jobId];
          const isStale =
            !meta?.fetchedAt || Date.now() - meta.fetchedAt > get().staleMs;
          if (!meta || isStale) {
            await get().fetchFirstPage(jobId, limit);
          }
        },
        fetchAllDaySequencesByJobId: async (jobId, limit = 100) => {
          await get().fetchFirstPage(jobId, limit);
          while (get().page[jobId]?.hasMore) {
            await get().fetchNextPage(jobId, limit);
          }
        },
        fetchDaySequencesForJobIds: async (jobIds: string[]) => {
          if (jobIds.length === 0) {
            return;
          }
          try {
            const daySequences =
              await daySequenceActions.getDaySequencesByJobIds(jobIds);
            if (daySequences.length > 0) {
              get().upsertMany(daySequences);
            }
          } catch (error) {
            console.error(
              'Error fetching day sequences for job ids batch:',
              error
            );
          }
        },
        fetchAllDaySequencesForClient: async (clientId: string) => {
          try {
            set({ isLoading: true });
            const daySequences =
              await daySequenceActions.getDaySequencesForClient(clientId);
            if (daySequences.length > 0) {
              get().upsertMany(daySequences);
            }
          } catch (error) {
            console.error('Error fetching day sequences for client:', error);
          } finally {
            set({ isLoading: false });
          }
        },
        fetchAllDaySequencesForUser: async (
          userId: string,
          statuses?: DaySequenceStatus[]
        ) => {
          try {
            set({ isLoading: true });
            const daySequences =
              await daySequenceActions.getDaySequencesForUser(
                userId,
                statuses ?? undefined
              );
            if (daySequences.length > 0) {
              get().upsertMany(daySequences);
            }
          } catch (error) {
            console.error('Error fetching day sequences for user:', error);
          } finally {
            set({ isLoading: false });
          }
        },
        fetchAllDaySequencesRequiringAction: async (tenantId: string) => {
          try {
            set({ isLoading: true });
            const daySequences =
              await daySequenceActions.getDaySequencesRequiringAction(tenantId);
            if (daySequences.length > 0) {
              get().upsertMany(daySequences);
            }
          } catch (error) {
            console.error('Error fetching day sequences for tenant:', error);
          } finally {
            set({ isLoading: false });
          }
        },
        clockAction: async (
          jobId: string,
          clockInTimestamp?: string,
          clockOutTimestamp?: string,
          breaks?: BreakEvent[],
          clockInGeo?: ClockGeoRecordInput,
          clockOutGeo?: ClockGeoRecordInput
        ): Promise<ClockActionResult> => {
          const result = await daySequenceActions.clockAction(
            jobId,
            clockInTimestamp,
            clockOutTimestamp,
            breaks,
            clockInGeo,
            clockOutGeo
          );
          if (result.success) {
            // clockAction returns one row; overnight / propose splits may create more
            await get().fetchAllDaySequencesByJobId(jobId);
          }
          return result;
        },

        approveDaySequences: async (ids: string[]): Promise<DaySequence[]> => {
          const daySequences = await daySequenceActions.approveDaySequences(
            ids
          );
          if (daySequences.length > 0) {
            get().upsertMany(daySequences);
          }
          return daySequences;
        },
        disputeProposedChange: async (
          ids: string[]
        ): Promise<DaySequence[]> => {
          const daySequences = await daySequenceActions.disputeDaySequences(
            ids
          );
          if (daySequences.length > 0) {
            get().upsertMany(daySequences);
          }
          return daySequences;
        },
        proposeDaySequenceChanges: async (
          daySequenceChanges: DaySequenceChangeInput[]
        ): Promise<DaySequence[]> => {
          const daySequences =
            await daySequenceActions.proposeDaySequenceChanges(
              daySequenceChanges
            );
          if (daySequences.length > 0) {
            get().upsertMany(daySequences);
          }
          return daySequences;
        },
        resolveDaySequenceDisputes: async (
          daySequenceChanges: DaySequenceChangeInput[]
        ): Promise<DaySequence[]> => {
          const daySequences =
            await daySequenceActions.resolveDaySequenceDisputes(
              daySequenceChanges
            );
          if (daySequences.length > 0) {
            get().upsertMany(daySequences);
          }
          return daySequences;
        },
        createDaySequences: async (
          input: CreateDaySequenceInput
        ): Promise<DaySequence[]> => {
          const daySequences = await daySequenceActions.createDaySequences(
            input
          );
          if (daySequences.length > 0) {
            get().upsertMany(daySequences);
          }
          return daySequences;
        },

        // Selectors
        getDaySequenceById: (id: string) => get().byId[id],
        getDaySequenceByJobId: (jobId: string) =>
          get()
            .idsByJobId[jobId]?.map((id) => get().byId[id])
            .filter(Boolean) || [],
        getDaySequenceByJobIdAndDateISO: (jobId: string, dateISO: string) => {
          const sequences = get().getDaySequenceByJobId(jobId);
          return (
            mergeDaySequencesForDateISO(sequences, dateISO) ??
            sequences.find(
              (daySequence) => daySequence.startDateISO === dateISO
            )
          );
        },

        getDaySequencesMeta: (jobId: string) =>
          get().page[jobId] || { hasMore: true, isLoading: false, error: null },

        getTodayDaySequenceByJobId: (jobId: string) => {
          const sequences = get().getDaySequenceByJobId(jobId);
          return mergeDaySequencesForTodayStatusPanel(sequences);
        },
        getLatestDaySequenceByJobId: (jobId: string) => {
          const sequences = get().getDaySequenceByJobId(jobId);
          return mergeDaySequencesForLatestContext(sequences);
        },
        getDaySequencesForClient: (clientId: string) => {
          const daySequences = Object.values(get().byId);
          return daySequences.filter(
            (daySequence) => daySequence.clientId === clientId
          );
        },
        getDaySequencesForClientByStatus: (
          clientId: string,
          status: DaySequenceStatus
        ) => {
          const daySequences = get().getDaySequencesForClient(clientId);
          return daySequences.filter(
            (daySequence) => daySequence.status === status
          );
        },

        getDaySequencesForClientByStatusGroupedByJob: (
          clientId: string,
          status: TimesheetStatusGroup
        ) => {
          const daySequences = get().getDaySequencesForClient(clientId);
          const groupedStatuses = {
            [TimesheetStatusGroup.PENDING]: [
              DaySequenceStatus.RequiresApproval,
            ],
            [TimesheetStatusGroup.DISPUTED]: [
              DaySequenceStatus.EscalatedToTenant,
              DaySequenceStatus.ProposedChange,
            ],
            [TimesheetStatusGroup.APPROVED]: [
              DaySequenceStatus.ReadyForPay,
              DaySequenceStatus.PayProcessed,
              DaySequenceStatus.Completed,
            ],
          };

          const groupedDaySequences = daySequences.reduce(
            (acc, daySequence) => {
              if (groupedStatuses[status].includes(daySequence.status)) {
                acc[daySequence.jobId] = [
                  ...(acc[daySequence.jobId] || []),
                  daySequence,
                ];
              }
              return acc;
            },
            {} as Record<string, DaySequence[]>
          );

          return groupedDaySequences;
        },

        getDaySequencesForActiveAndCompletedJobs: () => {
          const inProgressJobs = useWorkerJobStore
            .getState()
            .getInProgressJobs();
          const completedJobs = useWorkerJobStore.getState().getCompletedJobs();

          const allRelevantJobs = [...inProgressJobs, ...completedJobs];
          const uniqueJobIds = [
            ...new Set(allRelevantJobs.map((job) => job.id)),
          ];

          return uniqueJobIds
            .flatMap((jobId) => get().getDaySequenceByJobId(jobId))
            .sort((a, b) => Number(b.startDate) - Number(a.startDate));
        },

        getDaySequencesForWorkerAction: () => {
          const daySequences = get().getDaySequencesForActiveAndCompletedJobs();
          return daySequences.filter((daySequence) =>
            [DaySequenceStatus.InProgress].includes(daySequence.status)
          );
        },
        getDaySequencesForUser: (
          userId: string,
          statuses?: DaySequenceStatus[]
        ) => {
          const daySequences = Object.values(get().byId);
          return daySequences.filter(
            (daySequence) =>
              daySequence.userId === userId &&
              (statuses ? statuses.includes(daySequence.status) : true)
          );
        },
      }),
      {
        name: 'day-sequence-job-storage',
        storage: createJSONStorage(() => AsyncStorage),
        partialize: (state: DaySequenceStore) => ({
          byId: state.byId,
          idsByJobId: state.idsByJobId,
          staleMs: state.staleMs,
        }),
        onRehydrateStorage: () => (state) => {
          // Page and inFlight used to be persisted, but are not anymore because
          // of a stale data issue.
          // See https://zstaffing.atlassian.net/browse/ZF-280
          // Reset page and inFlight when rehydrating so we don't move stale
          // page and inFlight data into the store. By default, Zustand will move
          // persisted data into the store. This could persist their poisoned cache.
          // we want to stop this.
          if (state) {
            state.page = {};
            state.inFlight = {};
          }
        },
      }
    )
  )
);
